import { useEffect } from 'react';

// Flag global para evitar doble inyección del CSS del theme
let themeStyleInjected = false;

/**
 * Componente que inyecta en el <head> los estilos del theme
 * provenientes del campo `css` de theme_templates.json.
 *
 * El campo `css` contiene HTML con etiquetas <link> (rel="preload"
 * as="style", rel="stylesheet", etc.). Se parsea sin ejecutarlo y se
 * insertan los <link> resultantes en el <head> del documento.
 *
 * Sustituye al script hardcodeado que antes vivía en public/index.html.
 *
 * @param {string} css - HTML con las etiquetas <link> del theme.
 */
export default function ThemeStyle({ css }) {
  useEffect(() => {
    if (themeStyleInjected) return;
    if (!css) return;

    // Evitar duplicados si el efecto se re-ejecuta (StrictMode)
    if (document.querySelector('[data-theme-style]')) return;

    // Parsear el HTML del campo css sin ejecutarlo
    const doc = new DOMParser().parseFromString(css, 'text/html');
    const links = doc.querySelectorAll('link');

    if (links.length === 0) return;

    const head = document.head;
    let index = 0;

    links.forEach((sourceLink) => {
      const link = document.createElement('link');

      // Copiar atributos (rel, href, as, media, crossorigin, etc.)
      Array.from(sourceLink.attributes).forEach((attr) => {
        link.setAttribute(attr.name, attr.value);
      });

      // Un <link rel="preload" as="style"> solo descarga el CSS, no lo aplica.
      // Al cargar, se cambia a rel="stylesheet" para que el estilo surta efecto.
      const isStylePreload =
        link.getAttribute('rel') === 'preload' &&
        link.getAttribute('as') === 'style';

      if (isStylePreload) {
        link.addEventListener(
          'load',
          function handlePreloadLoad() {
            this.rel = 'stylesheet';
          },
          { once: true }
        );
      }

      link.dataset.themeStyle = String(index++);
      head.appendChild(link);
    });

    themeStyleInjected = true;
  }, [css]);

  return null;
}
